/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import {
  Chip,
  ChipGroup,
  Grid,
  Link,
  PageGridContainer,
  SelectOption,
  Typography2,
  breakpoints,
  typography2,
} from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import RoundedSelectField from 'bundles/unified-common/components/RoundedSelectField';
import PathwaysDataBox from 'bundles/unified-description-page-common/components/modules/career-outcomes/PathwaysDataBox';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { useCareerRolesData } from 'bundles/unified-description-page-common/contexts/CareerRolesDataContext';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { SkillCareerRoleFragment as CareerRoleFragment } from 'bundles/unified-description-page-common/queries/__generated__/CareerRolesQuery';

import _t from 'i18n!nls/xdp';

type Props = {
  anchorId?: string;
  careerGrowthInPercentage?: number | null;
  careerRoles?: CareerRoleFragment[];
  valueProps?: string[];
};

const styles = {
  moduleContainer: (enableXDPRedesign: boolean) => css`
    width: 100%;
    ${enableXDPRedesign ? xdpStyles.border('border') : '1px solid var(--cds-color-grey-50);'}
    border: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    border-radius: calc(var(--cds-border-radius-200) + var(--cds-border-radius-50));

    ${breakpoints.down('sm')} {
      border: none;
      padding: 0;
    }
  `,
  gridItem: css`
    padding: var(--cds-spacing-400) var(--cds-spacing-600);
    display: flex;
    flex-direction: column;
    justify-content: center;

    ${breakpoints.down('sm')} {
      padding: 0 0 var(--cds-spacing-200);
    }
  `,
  careerRolesSelect: css`
    margin-bottom: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
  `,
  valueProps: css`
    list-style: disc;
    margin-top: var(--cds-spacing-150);
    margin-left: var(--cds-spacing-300);
    ${typography2.bodyPrimary}

    li {
      margin-top: calc(var(--cds-spacing-100) / 2);
    }
  `,
  rdpLink: css`
    margin-top: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-top: calc(var(--cds-spacing-200) + var(--cds-spacing-50));
      padding: var(--cds-spacing-100) 0;
    }
  `,
};

const MAX_NUMBER_OF_ROLE_CHIPS = 2;

const PathwaysCareerOutcomesModule = ({ anchorId, careerRoles, valueProps, careerGrowthInPercentage }: Props) => {
  const eventingProductData = useEventingProductData();
  const { eventingPathwaysData } = useCareerRolesData();
  const { enableXDPRedesignPhase1, enableXDPRedesignDescriptionRework } = useXDPRedesignPhase1Data();
  const enableXDPRedesign = enableXDPRedesignPhase1 || enableXDPRedesignDescriptionRework;
  const track = useTracker();
  const visibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'career_info',
      },
      ...eventingProductData,
    }
  );

  const careerRolesData = useMemo(() => careerRoles?.filter((role) => Boolean(role.name)), [careerRoles]);
  const [selectedRole, setSelectedRole] = useState<CareerRoleFragment | null>(careerRolesData?.[0] ?? null);
  const pathwaysDataBoxVisibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_role_card',
    {
      role: {
        roleSlug: selectedRole?.slug ?? '',
      },
      productCard: {
        index: careerRolesData?.findIndex((role) => role.id === selectedRole?.id) ?? 0,
        recommender: eventingPathwaysData?.recommender,
      },
      carousel: eventingPathwaysData?.carousel,
    }
  );

  const handleRoleFilterClick = (roleId: string) => {
    const targetRole = careerRolesData?.find((role) => role.id === roleId);
    setSelectedRole(targetRole || null);

    if (!targetRole) return;
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: `collections_filter_chip_${targetRole.slug.replaceAll('-', '_')}`,
      },
    });
  };

  const handleExploreRoleLinkClick = useCallback<() => $TSFixMe>(() => {
    if (!careerRolesData || !selectedRole) return;

    track('click_button', {
      button: {
        name: 'role_info',
        linkName: 'description_page_role_link',
      },
      ...(selectedRole.slug
        ? {
            role: {
              roleSlug: selectedRole.slug,
            },
          }
        : {}),
      productCard: {
        index: careerRolesData.findIndex((role) => role.id === selectedRole.id),
        recommender: eventingPathwaysData?.recommender,
      },
      carousel: eventingPathwaysData?.carousel,
    });
  }, [track, selectedRole, eventingPathwaysData, careerRolesData]);

  const renderRoleFilter = () => {
    if (!careerRolesData) return null;

    const numberOfRoles = careerRolesData.length;

    if (numberOfRoles <= 1) return null;

    if (numberOfRoles <= MAX_NUMBER_OF_ROLE_CHIPS) {
      return (
        <ChipGroup aria-label={_t('Career Roles')} css={styles.careerRolesSelect}>
          {careerRolesData.map((careerRole) => (
            <Chip
              key={careerRole.id}
              selected={selectedRole?.id === careerRole.id}
              onClick={() => handleRoleFilterClick(careerRole.id)}
            >
              {careerRole.name as string}
            </Chip>
          ))}
        </ChipGroup>
      );
    }

    return (
      <RoundedSelectField
        value={selectedRole?.id}
        aria-label={_t('Career Role Selector')}
        onChange={(e) => handleRoleFilterClick(e.target.value as string)}
        css={styles.careerRolesSelect}
      >
        {careerRolesData.map((careerRole) => (
          <SelectOption value={careerRole.id} key={careerRole.id}>
            {careerRole.name}
          </SelectOption>
        ))}
      </RoundedSelectField>
    );
  };

  if (!careerRolesData || !careerRolesData.length) return null;

  return (
    <Section anchorId={anchorId} ref={visibilityTrackerRef}>
      <PageGridContainer>
        <Grid item xs={12}>
          <div css={styles.moduleContainer(enableXDPRedesign)}>
            <Grid container>
              <Grid item xs={12} md={6} css={styles.gridItem}>
                {renderRoleFilter()}
                <Typography2 component="h2" variant="titleSmall">
                  {_t('Prepare for a career as a #{domainName}', { domainName: selectedRole?.name })}
                </Typography2>
                {valueProps && (
                  <ul css={styles.valueProps}>
                    {valueProps.map((valueProp, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <li key={index}>{valueProp}</li>
                    ))}
                  </ul>
                )}
                <div css={styles.rdpLink}>
                  <Link
                    variant="quiet"
                    typographyVariant="subtitleMedium"
                    href={`/career-academy/roles/${selectedRole?.slug}${
                      eventingPathwaysData?.carousel?.id ? `?recommenderId=${eventingPathwaysData?.carousel?.id}` : ''
                    }`}
                    icon={<ArrowNextIcon />}
                    iconPosition="after"
                    onClick={handleExploreRoleLinkClick}
                  >
                    {_t('Explore this role')}
                  </Link>
                </div>
              </Grid>
              <Grid item xs={12} md={6}>
                <PathwaysDataBox
                  ref={pathwaysDataBoxVisibilityTrackerRef}
                  imageSrc={selectedRole?.careerOutcomesModuleImage?.url}
                  mobileImageSrc={selectedRole?.careerOutcomesModuleImageMobile?.url}
                  borderColor={selectedRole?.imagePrimaryColor}
                  jobTitle={selectedRole?.name}
                  medianSalary={selectedRole?.jobPostingData?.medianSalary}
                  numJobsAvailable={selectedRole?.jobPostingData?.numJobsAvailable}
                  countryName={selectedRole?.jobPostingData?.countryName}
                  currencyCode={selectedRole?.jobPostingData?.currencyCode}
                  careerGrowthInPercentage={careerGrowthInPercentage}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
      </PageGridContainer>
    </Section>
  );
};

export default PathwaysCareerOutcomesModule;
